/**
 * College List Sync Utility
 * Syncs college list between localStorage and backend database
 */

/**
 * Save college list to backend
 */
export const saveCollegeListToBackend = async (collegeList, authToken, apiBaseUrl) => {
  if (!authToken) {
    console.log('[CollegeListSync] Skipping backend save - no auth token');
    return false;
  }

  // Use relative URL if no base URL provided (will go through Amplify proxy)
  const apiUrl = apiBaseUrl 
    ? `${apiBaseUrl}/api/college-list`
    : '/api/college-list';

  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        collegeList: collegeList.map(college => ({
          name: college.name,
          location: college.location || '',
          acceptanceRate: college.acceptanceRate || null,
          satRange: college.satRange || null,
          stats: college.stats || {},
          status: college.status || 'interested',
          applicationDate: college.applicationDate || null
        }))
      })
    });

    if (response.ok) {
      console.log('[CollegeListSync] Successfully saved college list to backend');
      return true;
    } else {
      const errorText = await response.text();
      console.error('[CollegeListSync] Failed to save to backend:', response.status, response.statusText, errorText);
      return false;
    }
  } catch (error) {
    console.error('[CollegeListSync] Error saving to backend:', error);
    return false;
  }
};

/**
 * Load college list from backend
 */
export const loadCollegeListFromBackend = async (authToken, apiBaseUrl) => {
  if (!authToken) {
    console.log('[CollegeListSync] Skipping backend load - no auth token');
    return null;
  }

  // Use relative URL if no base URL provided (will go through Amplify proxy)
  const apiUrl = apiBaseUrl 
    ? `${apiBaseUrl}/api/college-list`
    : '/api/college-list';

  try {
    const response = await fetch(apiUrl, {
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    });

    if (response.ok) {
      const data = await response.json();
      const backendList = data.collegeList || [];
      
      console.log('[CollegeListSync] Loaded college list from backend:', backendList.length, 'colleges');
      return backendList;
    } else {
      // If 404 or other error, return empty array (graceful degradation)
      if (response.status === 404) {
        console.log('[CollegeListSync] No college list found in backend, returning empty array');
        return [];
      }
      console.error('[CollegeListSync] Failed to load from backend:', response.status);
      return null;
    }
  } catch (error) {
    console.error('[CollegeListSync] Error loading from backend:', error);
    return null;
  }
};

/**
 * Sync college list: merge localStorage and backend, save to both
 */
export const syncCollegeList = async (localList, authToken, apiBaseUrl) => {
  // Always save to localStorage first
  localStorage.setItem('myCollegeList', JSON.stringify(localList));

  // Try to load from backend
  const backendList = await loadCollegeListFromBackend(authToken, apiBaseUrl);

  if (backendList && backendList.length > 0) {
    // Merge: combine both lists, prefer local if there are conflicts
    const mergedList = [...backendList];
    localList.forEach(localCollege => {
      if (!mergedList.find(c => c.name === localCollege.name)) {
        mergedList.push(localCollege);
      }
    });

    // Save merged list to both
    localStorage.setItem('myCollegeList', JSON.stringify(mergedList));
    await saveCollegeListToBackend(mergedList, authToken, apiBaseUrl);
    
    return mergedList;
  } else {
    // No backend data, just save local to backend
    await saveCollegeListToBackend(localList, authToken, apiBaseUrl);
    return localList;
  }
};

